import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { verifyAdmin } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      const user = await verifyAdmin();
      if (user) {
        navigate('/admin/dashboard');
      }
      setChecking(false);
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('bormula_token', res.data.token);
      localStorage.setItem('bormula_username', res.data.username);
      localStorage.setItem('bormula_role', res.data.role || 'admin');
      localStorage.setItem('bormula_writer_name', res.data.writer_name || '');
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (err) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#CCFF00] rounded-lg flex items-center justify-center">
              <Lock size={24} className="text-black" />
            </div>
            <span className="font-display font-bold text-3xl tracking-tighter">BORMULA <span className="text-[#CCFF00]/50 text-sm">ADMIN</span></span>
          </div>
        </div>
        
        <form onSubmit={handleLogin} className="bg-[#111111] border border-white/10 p-10 rounded-[40px]">
          <h2 className="text-2xl font-display font-bold uppercase mb-8">
            Authentication Required
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">Username</label>
              <input 
                type="text" 
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-black border border-white/10 px-6 py-4 rounded-xl focus:border-[#CCFF00] outline-none text-white font-bold"
                placeholder="Enter Username"
                required
                data-testid="username-input"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">Password</label>
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black border border-white/10 px-6 py-4 rounded-xl focus:border-[#CCFF00] outline-none text-white font-bold"
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
            </div>

            {error && (
              <p className="text-red-500 text-xs font-bold uppercase">{error}</p>
            )}
            
            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black py-4 rounded-xl font-black uppercase tracking-widest hover:scale-[1.02] transition-all flex items-center justify-center"
              data-testid="submit-button"
            >
              {loading ? <span className="spinner border-black border-t-transparent" /> : 'Enter Command Center'}
            </button>
          </div>
        </form>

        <a 
          href="/"
          className="block w-full mt-8 text-center text-white/40 text-xs font-bold uppercase tracking-widest hover:text-[#CCFF00] transition-colors"
        >
          Back to Public Site
        </a>
      </div>
    </div>
  );
}
